import * as Yup from "yup";
import Alert from "../../utils/Alert.js";
import {
  addNewAdminService,
  editAdminService,
  editCustomerService,
} from "../../services/usersServices.js";

export const initialValues = {
  name: "",
  family: "",
  phone_number: "",
  email: "",
  password: "",
  confirm_password: "",
  address: "",
  address_code: "",
  national_code: "",
  commission_percentage: 0,
  is_admin: true,
  role: "",
  company_name: "",
  company_national_id: "",
  company_registration_number: "",
  company_economic_code: "",
  company_province: "",
  company_city: "",
  company_registration_address: "",
  company_registration_phone_number: "",
  company_shaba: "",
  company_postal_code: "",
  isEditing: false,
};

export const initialTouched = {
  name: true,
  family: true,
  phone_number: true,
  email: true,
  address: true,
  address_code: true,
  national_code: true,
  commission_percentage: true,
  is_admin: true,
  role: true,
  company_name: true,
  company_national_id: true,
  company_registration_number: true,
  company_economic_code: true,
  company_province: true,
  company_city: true,
  company_registration_address: true,
  company_registration_phone_number: true,
  company_shaba: true,
  company_postal_code: true,
};

export const onSubmit = async (
  values,
  actions,
  editingUser,
  setData,
  setAdmins,
  isAdminMode,
  navigate,
) => {
  // "isEditing" is an additional field to handle the validation for editing.
  const { isEditing, ...data } = values;
  try {
    if (editingUser) {
      // editing admin or customer has no password field.
      const {
        password,
        confirm_password,
        address,
        address_code,
        is_admin,
        ...editData
      } = data;
      if (isAdminMode) {
        const response = await editAdminService(editingUser.id, editData);
        if (response.status === 200) {
          setAdmins((prevAdmins) =>
            prevAdmins.map((a) => {
              if (a.id === editingUser.id) {
                return response.data;
              }
              return a;
            }),
          );
          await Alert("انجام عملیات", "ادمین با موفقیت ویرایش شد", "success");
          return navigate(-1);
        }
      } else {
        // customers don't have "role" field.
        const {
          role,
          national_code,
          commission_percentage,
          company_name,
          company_national_id,
          company_registration_number,
          company_economic_code,
          company_city,
          company_province,
          company_registration_address,
          company_registration_phone_number,
          company_shaba,
          company_postal_code,
          ...customerData
        } = editData;
        const response = await editCustomerService(
          editingUser.id,
          customerData,
        );
        if (response.status === 200) {
          setData((prevData) =>
            prevData.map((d) => {
              if (d.id === editingUser.id) {
                return response.data;
              }
              return d;
            }),
          );
          await Alert("انجام عملیات", "مشتری با موفقیت ویرایش شد", "success");
          return navigate(-1);
        }
      }
    } else {
      const response = await addNewAdminService(data);
      if (response.status === 201) {
        setAdmins((prevUsers) => [...prevUsers, response.data]);
        Alert("انجام عملیات", "ادمین با موفقیت اضافه شد", "success");
        actions.resetForm();
      }
    }
  } catch (error) {
    if (error.response && error.response.status === 400) {
      Alert(
        `خطا ${error.response.status}!`,
        "ایمیل یا شماره تلفن وارد شده در حال حاضر وجود دارد.",
        "error",
      );
    }
  }
};

export const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("لطفاً نام را وارد کنید")
    .matches(
      /^[a-zA-Z\u0600-\u06FF\u200c\s]+$/,
      "فقط از حروف الفبای فارسی یا انگلیسی استفاده شود",
    ),
  family: Yup.string()
    .required("لطفاً نام خانوادگی را وارد کنید")
    .matches(
      /^[a-zA-Z\u0600-\u06FF\u200c\s]+$/,
      "فقط از حروف الفبای فارسی یا انگلیسی استفاده شود",
    ),
  phone_number: Yup.string()
    .required("لطفاً شماره تلفن را وارد کنید")
    .matches(/^[0-9]{11}$/, "لطفاً یک شماره تلفن معتبر وارد کنید"),
  email: Yup.string()
    .required("لطفاً ایمیل را وارد کنید")
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z.]+\.[a-zA-Z]{2,}$/,
      "لطفاً یک ایمیل معتبر وارد کنید",
    ),
  address: Yup.string().when("is_admin", {
    is: false,
    then: (schema) => schema.required("لطفاً آدرس مشتری را وارد کنید"),
  }),
  address_code: Yup.string()
    .matches(/^[0-9]{10}$/, "کد پستی باید 10 رقمی باشد")
    .when("is_admin", {
      is: false,
      then: (schema) => schema.required("لطفاً کد پستی مشتری را وارد کنید"),
    }),
  password: Yup.string().when("isEditing", {
    is: false,
    then: (schema) =>
      schema
        .required("لطفاً رمز عبور را وارد کنید")
        .matches(/^[a-zA-Z0-9_]+$/, "داخل رمز از نمادها و فاصله استفاده نکنید"),
  }),
  confirm_password: Yup.string().when("isEditing", {
    is: false,
    then: (schema) =>
      schema
        .required("لطفاً تکرار رمز عبور را وارد کنید")
        .oneOf([Yup.ref("password")], "با رمز عبور مطابقت ندارد"),
  }),
  is_admin: Yup.boolean(),
  role: Yup.string().when("is_admin", {
    is: true,
    then: (schema) => schema.required("لطفاً یک نقش برای ادمین انتخاب کنید"),
  }),
  national_code: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا کد ملی را وارد کنید")
        .matches(/^[0-9]*$/, "کد ملی باید فقط شامل ارقام باشد.")
        .max(10, "کد ملی نباید بیشتر از ۱۰ رقم باشد."),
  }),
  company_name: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا نام شرکت را وارد کنید.")
        .matches(
          /^[a-zA-Z\u0600-\u06FF\u200c\s]+$/,
          "فقط از حروف الفبای فارسی یا انگلیسی استفاده شود",
        )
        .max(50, "حداکثر ۵۰ حرف مجاز است"),
  }),
  company_national_id: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا شناسه ملی را وارد کنید")
        .matches(/^[0-9]*$/, "شماره ملی باید فقط شامل ارقام باشد")
        .max(11, "شناسه ملی نباید بیشتر از ۱۱ رقم باشد."),
  }),
  company_registration_number: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا شماره ثبت را وارد کنید")
        .matches(/^[0-9]*$/, "شماره ثبت باید فقط شامل ارقام باشد")
        .max(10, "شماره ثبت نباید بیشتر از ۱۰ رقم باشد."),
  }),
  company_economic_code: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا کد اقتصادی را وارد کنید")
        .matches(/^[0-9]*$/, "کد اقتصادی باید فقط شامل ارقام باشد.")
        .max(12, "کد اقتصادی نباید بیشتر از ۱۲ رقم باشد."),
  }),
  company_province: Yup.string().when("is_admin", {
    is: true,
    then: (schema) => schema.required("لطفا استان را انتخاب کنید"),
  }),
  company_city: Yup.string().when("is_admin", {
    is: true,
    then: (schema) => schema.required("لطفا نام شهر را وارد کنید"),
  }),
  company_registration_phone_number: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا شماره تلفن ثابت را وارد کنید")
        .matches(/^[0-9]*$/, "شماره تلفن ثابت باید فقط شامل ارقام باشد.")
        .max(11, "شماره تلفن ثابت نباید بیشتر از ۱۱ رقم باشد."),
  }),
  company_shaba: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا شماره شبا را وارد کنید")
        .matches(/^[0-9]*$/, "شماره شبا باید فقط شامل ارقام باشد.")
        .max(24, "شماره شبا نباید بیشتر از ۲۴ رقم باشد."),
  }),
  company_postal_code: Yup.string().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا کد پستی شرکت را وارد کنید")
        .matches(/^[0-9]*$/, "کد پستی باید فقط شامل ارقام باشد.")
        .max(10, "کد پستی نباید بیش از ۱۰ رقم باشد."),
  }),
  commission_percentage: Yup.number().when("is_admin", {
    is: true,
    then: (schema) =>
      schema
        .required("لطفا درصد کمیسیون را وارد کنید")
        .min(0, "لطفا یک عدد بین ۰ تا ۱۰۰ وارد کنید")
        .max(100, "لطفا یک عدد بین ۰ تا ۱۰۰ وارد کنید"),
  }),
});

